import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanMatchFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { LoggingService } from '@app/core/services/logging/logging.service';
import { FirebaseAuthenticationService } from '@app/core/services/firebase/firebase-authentication/firebase-authentication.service';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { BookingService } from '../services/backend/booking.service';

const SYNC_STATE_KEY = 'syncState';

/**
 * Max time a sync is allowed to stay in the `isExecuting` state. If the request
 * is cancelled (page abandoned, navigation, network drop, ...) the persisted
 * state would otherwise stay stuck on `isExecuting: true` forever, blocking any
 * future integration entry. After this window we consider it failed and retry.
 */
const SYNC_EXECUTION_TIMEOUT_MS = 2 * 60 * 1000;

/**
 * Detects PMS placeholders that were never interpolated by the integration
 * (e.g. `{{confirmation_code}}`, `[$LEAD_UID$]`, `[INTEGRATION_KEY]`). When a
 * host configures the deep link incorrectly these tokens reach us verbatim and
 * the sync request would fail, so we abort early instead.
 */
const UNREPLACED_PLACEHOLDER_PATTERN = /[$[\]{}]/;

function hasUnreplacedPlaceholder(value: string | null | undefined): boolean {
  return !!value && UNREPLACED_PLACEHOLDER_PATTERN.test(value);
}

/** Persisted so dataPreloadGuard can detect completion without polling ~5s after removeItem. */
function writeSyncStateTerminal(): void {
  localStorage.setItem(
    SYNC_STATE_KEY,
    JSON.stringify({
      isExecuting: false,
      isCompleted: true,
      integrationKey: null,
      integrationBookingId: null,
      syncStartedAt: null,
    }),
  );
}

@Injectable({
  providedIn: 'root',
})
export class SyncGuard implements CanActivate {
  constructor(
    private bookingService: BookingService,
    private router: Router,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    LoggingService.log('SyncGuard canActivate');

    return new Observable<boolean | UrlTree>((observer) => {
      const urlParams = new URLSearchParams(window.location.search);

      const integrationKey = urlParams.get('int');
      const integrationBookingId =
        urlParams.get('extid') || urlParams.get('extId');

      // Get or initialize sync state
      const syncState = JSON.parse(
        localStorage.getItem(SYNC_STATE_KEY) ||
          '{"isExecuting": false, "isCompleted": false, "integrationKey": null, "integrationBookingId": null, "syncStartedAt": null}',
      );

      // Recover from a sync that started but never finished. Without this a
      // cancelled request leaves isExecuting stuck on true and every future
      // integration entry is silently skipped.
      if (
        syncState.isExecuting &&
        (!syncState.syncStartedAt ||
          Date.now() - syncState.syncStartedAt > SYNC_EXECUTION_TIMEOUT_MS)
      ) {
        LoggingService.log('Stale sync execution detected, resetting state');
        syncState.isExecuting = false;
        syncState.syncStartedAt = null;
        localStorage.setItem(SYNC_STATE_KEY, JSON.stringify(syncState));
      }

      // If we have URL params, update sync state
      if (integrationKey && integrationBookingId) {
        syncState.integrationKey = integrationKey;
        syncState.integrationBookingId = integrationBookingId;
        syncState.isCompleted = false;
        syncState.isExecuting = false;
        syncState.syncStartedAt = null;
        localStorage.setItem(SYNC_STATE_KEY, JSON.stringify(syncState));
        // Only store route for redirect if not on login and no redirectTo already exists
        if (!window.location.pathname.includes('/login')) {
          // Store the full URL including query parameters for integration sync
          const routeToStore = window.location.pathname;
          localStorage.setItem('redirectTo', routeToStore);
        }
      }

      // Abort early when the host's deep link still contains unreplaced
      // placeholders. Running sync with these would break and bounce the user
      // to the generic error page, so we redirect to a dedicated message.
      if (
        !syncState.isCompleted &&
        (hasUnreplacedPlaceholder(syncState.integrationKey) ||
          hasUnreplacedPlaceholder(syncState.integrationBookingId))
      ) {
        LoggingService.error(
          'Invalid integration link detected, aborting sync',
          {
            integrationKey: syncState.integrationKey,
            integrationBookingId: syncState.integrationBookingId,
          },
        );
        writeSyncStateTerminal();
        localStorage.removeItem('redirectTo');
        localStorage.setItem('clearIntegrationParams', 'true');
        observer.next(
          this.router.parseUrl('/error?type=invalid-integration-link'),
        );
        observer.complete();
        return;
      }

      // Check if we should process sync
      if (
        syncState.integrationKey &&
        syncState.integrationBookingId &&
        !syncState.isExecuting &&
        !syncState.isCompleted
      ) {
        let syncStarted = false;
        const unsubscribeAuth = onAuthStateChanged(getAuth(), (user) => {
          const route = window.location.pathname;
          if (!user) {
            return;
          }

          // Prevent duplicate sync calls from repeated onAuthStateChanged emissions
          if (syncStarted) {
            return;
          }
          syncStarted = true;
          unsubscribeAuth();

          if (route !== '/login') {
            localStorage.setItem('clearIntegrationParams', 'true');
          }

          syncState.isExecuting = true;
          syncState.syncStartedAt = Date.now();
          localStorage.setItem(SYNC_STATE_KEY, JSON.stringify(syncState));
          const sync$ = this.bookingService.sync(
            syncState.integrationKey,
            syncState.integrationBookingId,
          );

          sync$
            .pipe(
              take(1),
              tap(() => {
                LoggingService.log('Sync successful');
                writeSyncStateTerminal();
              }),
              switchMap(() => of(true)),
              catchError((error) => {
                LoggingService.error('Sync failed:', error);
                writeSyncStateTerminal();
                return of(this.router.parseUrl('/error'));
              }),
            )
            .subscribe({
              next: (result) => {
                LoggingService.log('Sync completed:', result);
                observer.next(result);
              },
              error: (error) => {
                LoggingService.error('Sync error:', error);
                observer.error(error);
              },
              complete: () => {
                LoggingService.log('Sync complete');
                observer.complete();
              },
            });
        });

        return () => unsubscribeAuth();
      } else {
        const unsubscribeAuth = onAuthStateChanged(getAuth(), (user) => {
          unsubscribeAuth();

          if (user && integrationKey && integrationBookingId) {
            localStorage.setItem('clearIntegrationParams', 'true');
          }

          observer.next(true);
          observer.complete();
        });

        return () => unsubscribeAuth();
      }
    });
  }
}
